import React, { useEffect, useState } from 'react';
import Icon from './icons';
import { mval } from './collect';
import { uploadPreview, fileUrl } from './api';
import type { Collect, Master } from './types';

export type DocViewState =
  | { who: 'gp' | 'lp'; i: number; field: 'rrn' | 'addr'; docKey: string; mapKey: string; label: string }
  | { free: true; docKey: string; label: string; fileName?: string; s4field?: 'area' | 'owner' | 'ownerAddr'; s3key?: string };

interface Props {
  doc: DocViewState;
  collect: Collect;
  master: Master;
  editM: (fn: (m: Master) => void) => void;
  onClose: () => void;
}

export default function DocViewModal({ doc, collect, master, editM, onClose }: Props) {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const isFree = 'free' in doc;
  let fileName: string;
  let who: string;
  let val: string;
  let set: (v: string) => void;
  let noneMsg: string;
  let uploadId = '';
  let s3key = '';

  if (isFree) {
    const f4 = doc.s4field || 'area';
    fileName = doc.fileName || '';
    who = '조합';
    val = (master.s4 && master.s4[f4]) || '';
    set = (v) => editM((m) => { m.s4[f4] = v; });
    s3key = doc.s3key || '';
    noneMsg = '미업로드 — 3단계에서 ' + doc.docKey + ' 파일을 업로드하면 열람됩니다';
  } else {
    const card = doc.who === 'gp' ? collect.gp : collect.lps[doc.i];
    fileName = card ? mval(card, doc.mapKey) : '';
    const r = card && card.results ? card.results.find((x) => x.value === fileName && x.uploadId) : null;
    uploadId = (r && r.uploadId) || '';
    who = doc.who === 'gp' ? 'GP' : 'LP' + (doc.i + 1);
    val = doc.who === 'gp' ? master.gp[doc.field] : ((master.lps[doc.i] || { rrn: '', addr: '' }) as { rrn: string; addr: string })[doc.field];
    set = (v) => editM((m) => {
      if (doc.who === 'gp') m.gp[doc.field] = v;
      else if (m.lps[doc.i]) m.lps[doc.i][doc.field] = v;
    });
    noneMsg = '고객 미제출 — 1단계에서 ' + doc.docKey + ' 파일을 받아야 합니다';
  }

  useEffect(() => {
    setUrl(''); setZoom(1);
    let alive = true;
    if (uploadId) {
      setLoading(true);
      uploadPreview(uploadId)
        .then((r) => { if (alive) { setUrl(r.url || ''); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    } else if (s3key) {
      setLoading(true);
      fileUrl(s3key, fileName || 'file', true)
        .then((u) => { if (alive) { setUrl(u || ''); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { alive = false; };
  }, [uploadId, s3key, fileName]);

  const isImg = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(fileName || '');
  const wide = !!url;
  const zbtn: React.CSSProperties = { background: '#fff', border: '1px solid #c5cbd6', borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontSize: 13, lineHeight: 1.4, color: '#1a2438' };

  return (
    <div className="ovm">
      <div className="ovbox docbox" style={wide ? { maxWidth: 960, width: '94%' } : undefined}>
        <div className="ovh"><b>{who} · {doc.docKey} 미리보기</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>
        <div className="docprev">
          {fileName ? (
            url ? (
              isImg ? (
                <div style={{ position: 'relative', width: '100%', height: '54vh', overflow: 'auto', background: '#525659', borderRadius: 8 }}>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 2 }}>
                    <button type="button" onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))} style={zbtn}>－</button>
                    <button type="button" onClick={() => setZoom(1)} style={zbtn}>{Math.round(zoom * 100)}%</button>
                    <button type="button" onClick={() => setZoom((z) => Math.min(5, Math.round((z + 0.25) * 100) / 100))} style={zbtn}>＋</button>
                  </div>
                  <div style={{ minHeight: '100%', display: 'flex', alignItems: zoom > 1 ? 'flex-start' : 'center', justifyContent: zoom > 1 ? 'flex-start' : 'center', padding: 8, boxSizing: 'border-box' }}>
                    <img src={url} alt={fileName} onClick={() => setZoom((z) => (z > 1 ? 1 : 2))} style={{ width: zoom === 1 ? 'auto' : (zoom * 100) + '%', maxWidth: zoom === 1 ? '100%' : 'none', maxHeight: zoom === 1 ? '100%' : 'none', objectFit: 'contain', cursor: zoom > 1 ? 'zoom-out' : 'zoom-in' }} />
                  </div>
                </div>
              ) : (
                <iframe title={fileName} src={url} style={{ width: '100%', height: '54vh', border: '1px solid #e5e9f2', borderRadius: 8, background: '#525659' }} />
              )
            ) : (
              <div className="docimg">📄<div className="docfn">{fileName}</div><div className="dochint">{loading ? '미리보기 불러오는 중…' : '제출 파일 · 미리보기를 불러올 수 없습니다'}</div></div>
            )
          ) : (
            <div className="docnone">{noneMsg}</div>
          )}
        </div>
        <div className="docfield">
          <label>{doc.label} 입력 (미리보기 보며)</label>
          <input value={val || ''} onChange={(e) => set(e.target.value)} placeholder={doc.label} autoFocus />
        </div>
        <div className="docnote">바깥을 클릭해도 닫히지 않습니다 · [닫기]를 눌러 저장하고 닫으세요</div>
      </div>
    </div>
  );
}
